<script setup lang="ts">
/**
 * Upload-or-paste image control. Owns its own hidden file input and calls the
 * app-provided upload adapter, writing the resulting URL back through
 * `v-model`.
 *
 * `variant` only changes the preview shape: `avatar` is a round thumbnail,
 * `field` shows a URL input beside the button (used where the URL is
 * user-editable), `compact` is button + small thumbnail.
 *
 * Every variant can also paste a URL, not just `field` — a tenant whose
 * upload fails (or who already has the image hosted elsewhere, e.g. an
 * existing brand logo) still needs a way to set the image. `field` shows its
 * input inline as before; the other variants reveal a compact one behind a
 * toggle so their tight per-item layouts (array cards, avatar rows) don't
 * grow by default.
 *
 * `accept` defaults to the formats every consuming backend accepts — widen it
 * per-app if that app's uploader takes more (svg, gif, avif…).
 */
const props = withDefaults(defineProps<{
  modelValue: string | undefined
  label?: string
  variant?: 'field' | 'compact' | 'avatar' | 'wide' | 'logo'
  accept?: string
}>(), { variant: 'compact', accept: 'image/jpeg,image/png,image/webp' })

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const { upload } = useSiteEditorAdapters()
const t = useSiteEditorT()
const toast = useToast()

// `label` has no static default so its translated fallback can go through
// `t()` — a `withDefaults` default runs outside a context where composables
// are safe to call.
const resolvedLabel = computed(() => props.label ?? t('site.f.common.upload_label', 'Upload'))

const input = ref<HTMLInputElement | null>(null)
const uploading = ref(false)
// Starts open when the field already holds a URL a prior upload didn't
// produce (e.g. seeded from a template, or pasted before a reload), so the
// value stays visible and editable instead of hiding behind the toggle.
const showUrlInput = ref(props.variant !== 'field' && !!props.modelValue)

const pick = () => input.value?.click()

const onChange = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  uploading.value = true
  try {
    const result = await upload(file, 'image')
    emit('update:modelValue', result.url)
  } catch (e) {
    // Catching keeps the rejection from escaping as an unhandled error. An
    // adapter that already told the user (in its own words and locale) marks
    // the error `handled`, so we don't stack a second toast on top of it.
    if (!(e as { handled?: boolean })?.handled) {
      const message = (e as { data?: { message?: string } })?.data?.message
        || (e as Error)?.message
      toast.add({ title: message || t('site.f.common.upload_failed', 'Image upload failed.'), color: 'error' })
    }
  } finally {
    uploading.value = false
    target.value = ''
  }
}

const previewClass = computed(() => ({
  field: 'mt-2 max-h-32 rounded-lg object-cover',
  compact: 'size-8 rounded object-cover',
  avatar: 'size-8 rounded-full object-cover',
  wide: 'h-10 rounded object-cover',
  logo: 'h-8 w-auto rounded object-contain'
}[props.variant]))
</script>

<template>
  <div>
    <div class="flex items-center gap-2">
      <UButton
        :size="variant === 'field' ? 'sm' : 'xs'"
        variant="outline"
        color="neutral"
        icon="i-lucide-upload"
        :loading="uploading"
        @click="pick"
      >
        {{ resolvedLabel }}
      </UButton>
      <UButton
        v-if="variant !== 'field'"
        size="xs"
        variant="ghost"
        color="neutral"
        icon="i-lucide-link"
        :aria-label="t('site.f.common.paste_image_url', 'Paste image URL')"
        @click="showUrlInput = !showUrlInput"
      />
      <UInput
        v-if="variant === 'field'"
        :model-value="modelValue"
        :placeholder="t('site.f.common.url_placeholder_ellipsis', 'https://…')"
        class="min-w-0 flex-1 font-mono text-xs"
        @update:model-value="$emit('update:modelValue', String($event))"
      />
      <img
        v-else-if="modelValue && !showUrlInput"
        :src="modelValue"
        :class="previewClass"
      >
    </div>
    <UInput
      v-if="variant !== 'field' && showUrlInput"
      :model-value="modelValue"
      :placeholder="t('site.f.common.url_placeholder_ellipsis', 'https://…')"
      class="mt-2 w-full font-mono text-xs"
      @update:model-value="$emit('update:modelValue', String($event))"
    />
    <img
      v-if="modelValue && (variant === 'field' || showUrlInput)"
      :src="modelValue"
      :class="previewClass"
    >
    <input
      ref="input"
      type="file"
      :accept="accept"
      class="hidden"
      @change="onChange"
    >
  </div>
</template>
